import { type ClassValue, clsx } from "clsx";
import copy from "copy-to-clipboard";
import { formatDistanceToNow } from "date-fns";
import { enUS } from "date-fns/locale";
import { twMerge } from "tailwind-merge";
import { v4 as UUID, validate } from "uuid";
import * as v from "valibot";
import { SITE_API_URL, SITE_REPORT_EMAIL, SITE_URL } from "./constants";

export const getSiteUrl = () => {
	return process.env.NODE_ENV === "development"
		? "http://127.0.0.1:8080"
		: "https://test.zeekmaps.com";
};

export const getSiteApiUrl = () => {
	return process.env.NODE_ENV === "development"
		? "http://127.0.0.1:8787"
		: "https://test-api.zeekmaps.com";
};

export const REGEXP_ONLY_DIGITS = "^\\d+$";
export const REGEXP_ONLY_CHARS = "^[a-zA-Z]+$";
export const REGEXP_ONLY_DIGITS_AND_CHARS = "^[a-zA-Z0-9]+$";

export function cn(...inputs: ClassValue[]) {
	return twMerge(clsx(inputs));
}

export function slice(s: string | null | undefined, length: number) {
	if (!s) return null;
	if (s.length < length) {
		return s;
	}
	return `${s.slice(0, length)}..`;
}

// Get URL of site
export function getUrl() {
	if (process.env.NODE_ENV === "development") {
		return process.env.NEXT_PUBLIC_WWW_URL || "http://127.0.0.1:3000";
	}
	return process.env.NEXT_PUBLIC_WWW_URL || SITE_URL;
}

// Get Supabase auth redirect URL
export function getSupabaseRedirectUrl() {
	return (
		process.env.NEXT_PUBLIC_SUPABASE_AUTH_REDIRECT_URL ||
		"https://ihhnixctzgrhyqpyedgl.supabase.co/auth/v1/callback"
	);
}

export function getApiUrl() {
	if (process.env.NODE_ENV === "development") {
		return process.env.NEXT_PUBLIC_API_URL || "http://127.0.0.1:8080";
	}
	return process.env.NEXT_PUBLIC_API_URL || SITE_API_URL;
}

export function getSignInUrl() {
	return `${getUrl()}/signin`;
}

export function getBlogPostUrl(slug: string) {
	return `${getUrl()}/blog/${slug}`;
}

export function getBlogPostCommentsUrl(slug: string, commentId: string) {
	return `${getBlogPostUrl(slug)}#comment-${commentId}`;
}

export function getProfileLink(username: string) {
	return `${getUrl()}/profile/${username}`;
}

export function getBlogTagLink(tag: string) {
	return `${getUrl()}/blog?tags=${tag}`;
}

// Reading Time
export function getMinutes(minutes: number): string {
	const roundedMinutes = Math.round(minutes);
	return `${roundedMinutes} min`;
}

// Get readable number
export function getReadableNumber(num: number): string {
	return new Intl.NumberFormat("en", {
		notation: "compact",
		compactDisplay: "short",
	}).format(num);
}

const stringToEmojiCode = {
	love: { code: "2764-fe0f", emoji: "❤️" },
	like: { code: "1f44d", emoji: "👍" },
	dislike: { code: "1f44e", emoji: "👎" },
	wow: { code: "1f62e", emoji: "😮" },
	cry: { code: "1f622", emoji: "😢" },
	pray: { code: "1f64f", emoji: "🙏" },
	angry: { code: "1f621", emoji: "😡" },
};

// Get all reaction emoji codes
export function reactionEmojiCodes() {
	return Object.values(stringToEmojiCode).map((reaction) => reaction.code);
}

// Convert reaction string to emoji code
export function reactionToEmoji(reaction: string) {
	return stringToEmojiCode[reaction as keyof typeof stringToEmojiCode].code;
}

export function emojiToReaction(emoji: string) {
	return Object.keys(stringToEmojiCode).find(
		(key) =>
			stringToEmojiCode[key as keyof typeof stringToEmojiCode].code === emoji ||
			stringToEmojiCode[key as keyof typeof stringToEmojiCode].emoji === emoji,
	);
}

// Time ago
export const timeAgo = (date: Date) =>
	formatDistanceToNow(date, {
		addSuffix: true,
		locale: enUS,
	});

export function getClientId() {
	// Check if we're in a browser environment
	if (typeof localStorage === "undefined") return null;

	let clientId = localStorage.getItem("zeek_maps_client_uuid");
	if (!clientId) {
		clientId = UUID();
		localStorage.setItem("zeek_maps_client_uuid", clientId);
	}
	return clientId;
}

export function getReadingTime(article: string, wordsPerMinute = 200) {
	// Count words — split on whitespace, filter out empty
	const words = article.trim().split(/\s+/).filter(Boolean).length;
	const minutes = Math.max(1, Math.ceil(words / wordsPerMinute));
	const readingFinishDate = new Date(Date.now() + minutes * 60_000);
	return timeAgo(readingFinishDate);
}

export function parseSearchParam(
	param: string | string[] | undefined,
): string[] {
	if (typeof param === "string") {
		return param
			.split(",")
			.map((s) => s.trim())
			.filter(Boolean);
	}
	if (Array.isArray(param)) {
		return param
			.flatMap((v) => (typeof v === "string" ? v.split(",") : []))
			.map((s) => s.trim())
			.filter(Boolean);
	}
	return [];
}

export function scrollToElement(id: string, padding = 0) {
	const element = document.getElementById(id);
	if (element) {
		const targetPosition =
			element.getBoundingClientRect().top + window.scrollY - padding;
		window.scrollTo({ top: targetPosition, behavior: "smooth" });
	}
}

/**
 * Copy text to clipboard with fallback for mobile & unsupported browsers
 * @param text string to copy
 * @returns boolean (true if success, false otherwise)
 */
export async function copyToClipboard(text: string): Promise<boolean> {
	if (typeof navigator !== "undefined" && navigator.clipboard?.writeText) {
		try {
			await navigator.clipboard.writeText(text);
			return true;
		} catch {
			// fallback
		}
	}

	// fallback (works on iOS Safari, Android, etc.)
	return copy(text);
}

export function generateReportEmailLink(postId: string, commentId?: string) {
	if (!commentId) {
		const url = getBlogPostUrl(postId);
		return `mailto:${SITE_REPORT_EMAIL}?subject=Report%20Post&body=Reporting%20Post:%20${url}`;
	}

	const url = getBlogPostCommentsUrl(postId, commentId);
	return `mailto:${SITE_REPORT_EMAIL}?subject=Report%20Post%20Comment&body=Reporting%20Comment:%20${url}`;
}

export function isValidUUID(uuid: string): boolean {
	return validate(uuid);
}

export const getPublicUrl = (path: string) => {
	if (typeof window === "undefined") return ""; // avoid SSR errors
	return `${window.location.origin}${path.startsWith("/") ? path : `/${path}`}`;
};

export const generateNonce = async (): Promise<string[]> => {
	const nonce = btoa(
		String.fromCharCode(...crypto.getRandomValues(new Uint8Array(32))),
	);
	const encoder = new TextEncoder();
	const encodedNonce = encoder.encode(nonce);
	const hashBuffer = await crypto.subtle.digest("SHA-256", encodedNonce);
	const hashArray = Array.from(new Uint8Array(hashBuffer));
	const hashedNonce = hashArray
		.map((b) => b.toString(16).padStart(2, "0"))
		.join("");
	return [nonce, hashedNonce];
};

// Valibot schemas
export const NameSchema = v.union([
	v.pipe(
		v.string(),
		v.minLength(3, "Name must be at least 3 characters."),
		v.maxLength(50, "Name must be at most 50 characters."),
	),
]);
export const UsernamePattern = /^[a-z0-9_-]{4,16}$/iu;
export const UsernamePatternRegex = /@([a-zA-Z0-9_-]{4,16})/g;
export const UsernameSchema = v.pipe(
	v.string(),
	v.regex(UsernamePattern),
	v.title("Username"),
	v.description(
		"A username must be between 4 and 16 characters long and can only contain letters, numbers, underscores and hyphens.",
	),
);
export const EmailSchema = v.pipe(
	v.string(),
	v.nonEmpty("Please enter your email."),
	v.email("The email is badly formatted."),
	v.maxLength(30, "Your email is too long."),
);
// export const PhoneSchema = v.pipe(
// 	v.string(),
// 	v.minLength(1, "Enter phone number"),
// 	v.custom(
// 		(value) => isValidPhoneNumber(value as string),
// 		"Invalid phone number",
// 	),
// );
export const PasswordSchema = v.pipe(
	v.string(),
	v.minLength(8, "Your password is too short."),
	v.maxLength(30, "Your password is too long."),
	v.regex(/[a-z]/, "Your password must contain a lowercase letter."),
	v.regex(/[A-Z]/, "Your password must contain a uppercase letter."),
	v.regex(/[0-9]/, "Your password must contain a number."),
);
export const OptionalDescriptionSchema = v.union([
	v.pipe(
		v.string(),
		v.minLength(3, "Description must be at least 3 characters."),
		v.maxLength(150, "Description must be at most 150 characters."),
	),
	v.literal(""),
]);
export const OptionalMessageSchema = v.union([
	v.pipe(
		v.string(),
		v.minLength(3, "Message must be at least 3 characters."),
		v.maxLength(1000, "Message must be at most 1000 characters."),
	),
	v.literal(""),
]);
export const OptionalUrlSchema = v.union([
	v.pipe(v.string(), v.url()),
	v.literal(""),
]);
export const NumberSchema = v.union([
	v.number(),
	v.pipe(v.string(), v.decimal()),
]);
export const DateSchema = v.union([
	v.date(),
	v.pipe(v.string(), v.isoTimestamp()),
]);
export const OptionalImageFileSchema = v.union([
	v.optional(
		v.pipe(
			v.file("Please select an image file."),
			v.mimeType([
				"image/jpg",
				"image/jpeg",
				"image/png",
				"image/svg",
				"image/gif",
				"image/webp",
			]),
			v.maxSize(1024 * 1024 * 10, "Please select a file smaller than 10 MB."),
		),
	),
]);
export const ErrorSchema = v.instance(Error, "Error instance required.");
export const EmojiSchema = v.pipe(
	v.string(),
	v.emoji("Please provide a valid emoji."),
);

export function capitalizeFirstLetter(val: string) {
	return String(val).charAt(0).toUpperCase() + String(val).slice(1);
}
